import { useCallback, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ICONS } from '@/components/icons/icons'
import { ThemeToggle } from '@/components/theme/ThemeToggle'
import { authDisabled, env } from '@/config/env'
import { signOut } from '@/features/auth/identityAuth'
import { useMe } from '@/features/auth/MeContext'
import { SyncPanel } from '@/features/sync/SyncPanel'
import { formatPercent } from '@/lib/format'

import { monthRange } from '../lib/calendar'
import { isPeriodComplete, paceStatus, pctOfBudget } from '../lib/pace'
import { periodNoun } from '../lib/periodText'
import { useRunwayModel } from '../RunwayContext'
import { GlobalSearch } from './GlobalSearch'
import { NavItem } from './NavItem'
import styles from './Sidebar.module.css'

const SECTION_NAMES: Record<string, string> = {
  '/access': 'Access',
  '/settings/departments': 'Department settings',
  '/settings/accounts': 'Account settings',
}

async function handleSignOut(): Promise<void> {
  await signOut()
  window.location.assign('/')
}

function onSignOutClick(): void {
  void handleSignOut()
}

export function Sidebar() {
  const { runway, calendar, pacePct } = useRunwayModel()
  const complete = isPeriodComplete(runway)
  const me = useMe()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const toggle = useCallback(() => setOpen((value) => !value), [])
  const close = useCallback(() => setOpen(false), [])
  const quarterShort = runway.quarter_label
  // Lone Sage departments no tile groups (Sage parents roll up on their own).
  const autoCount = runway.department_order.filter(
    (code) => runway.departments[code]?.auto && runway.departments[code]?.grouping === 'sage',
  ).length

  const currentName = location.pathname.startsWith('/departments/')
    ? (runway.departments[decodeURIComponent(location.pathname.split('/')[2] ?? '')]?.name ?? 'All Departments')
    : (SECTION_NAMES[location.pathname] ?? 'All Departments')

  return (
    <aside className={`${styles.sidebar} ${open ? styles.open : ''}`}>
      <div className={styles.top}>
        <div className={styles.brand}>
          <div className={styles.eyebrow}>
            FP&amp;A · RBI Private Lending
            {env.appEnv === 'qa' ? <span className={styles.envBadge}>QA</span> : null}
            {env.useMocks ? <span className={styles.envBadge}>Mock data</span> : null}
          </div>
          <h1 className={styles.title}>Department Runway</h1>
          <div className={styles.sub}>Expenses vs. {quarterShort} Budget</div>
        </div>
        <button
          type="button"
          className={styles.menuButton}
          onClick={toggle}
          aria-expanded={open}
          aria-controls="sidebar-collapsible"
          aria-label={open ? 'Close department menu' : `Open department menu (viewing ${currentName})`}
        >
          {open ? <ICONS.close size={18} /> : <ICONS.menu size={18} />}
        </button>
      </div>

      <div className={styles.collapsible} id="sidebar-collapsible">
        <GlobalSearch departmentCount={runway.department_order.length} />

        <nav className={styles.navGroups} aria-label="Sections">
          <div>
            <h2 className={styles.groupLabel}>Company</h2>
            <ul className={styles.nav}>
              <li>
                <NavItem to="/" name="All Departments" status="accent" overview onNavigate={close} />
              </li>
            </ul>
          </div>

          <div>
            <h2 className={styles.groupLabel}>Departments</h2>
            <ul className={styles.nav}>
              {runway.department_order.map((code) => {
                const dept = runway.departments[code]
                if (!dept) return null
                const used = pctOfBudget(dept.qtd_actual, dept.quarter_budget)
                const status = paceStatus(used, pacePct)
                return (
                  <li key={code}>
                    <NavItem
                      to={`/departments/${encodeURIComponent(code)}`}
                      name={dept.name}
                      status={status}
                      figure={used === null ? 'n/b' : formatPercent(used)}
                      figureLabel={
                        used === null
                          ? 'no budget on file'
                          : `${formatPercent(used)} of budget used, ${status === 'good' ? (complete ? 'at or under budget' : 'on or under pace') : complete ? 'over budget' : 'over pace'}`
                      }
                      onNavigate={close}
                    />
                  </li>
                )
              })}
            </ul>
          </div>

          {me.permissions.canManageAccess || me.permissions.canConfigure ? (
            <div>
              <h2 className={styles.groupLabel}>Admin</h2>
              <ul className={styles.nav}>
                {me.permissions.canManageAccess ? (
                  <li>
                    <NavItem to="/access" name="Access" status="neutral" icon="users" onNavigate={close} />
                  </li>
                ) : null}
                {me.permissions.canConfigure ? (
                  <>
                    <li>
                      <NavItem
                        to="/settings/departments"
                        name="Departments"
                        status="neutral"
                        icon="grid"
                        figure={autoCount ? `${autoCount} new` : undefined}
                        figureLabel={autoCount ? `${autoCount} new Sage departments to group` : undefined}
                        onNavigate={close}
                      />
                    </li>
                    <li>
                      <NavItem
                        to="/settings/accounts"
                        name="Accounts"
                        status={runway.unclassified.count ? 'bad' : 'neutral'}
                        icon="ledger"
                        figure={runway.unclassified.count ? `${runway.unclassified.count} new` : undefined}
                        figureLabel={
                          runway.unclassified.count ? `${runway.unclassified.count} accounts unclassified` : undefined
                        }
                        onNavigate={close}
                      />
                    </li>
                  </>
                ) : null}
              </ul>
            </div>
          ) : null}
        </nav>

        <div className={styles.foot}>
          <b>{runway.period.label}</b> · {monthRange(calendar)} · as of {runway.as_of}
          <br />
          {complete
            ? 'Period complete: final figures'
            : `${formatPercent(pacePct)} of the ${periodNoun(runway.period)} elapsed`}
          <br />
          Source: Sage Intacct · read-only
          <SyncPanel lastSync={runway.last_sync} dataThrough={runway.data_through} />
          <div className={styles.user} title={me.email}>
            {me.name}
          </div>
          <div className={styles.footActions}>
            <ThemeToggle />
            {authDisabled ? null : (
              <button type="button" className={styles.signOut} onClick={onSignOutClick}>
                <ICONS.logout size={13} />
                Sign out
              </button>
            )}
          </div>
        </div>
      </div>
    </aside>
  )
}
